import React from "react";
import { Text, Flex, Button, Icon } from "@chakra-ui/react";

import {
  backgroundLight,
  primaryAlt,
  textSecondary,
  white,
} from "../../../../../utils/colors";
import { useTranslation } from "react-i18next";
import useThemeColors from "../../../../../hooks/useThemeColors";
import { FaCircleInfo } from "react-icons/fa6";
import dayjs from "dayjs";

// Dynamically set locale based on local storage value
const locale = localStorage.getItem("language") || "en"; // Default to 'en' if not set
dayjs.locale(locale);

const ActiveFilter = ({
  filterParams,
  setIsFilterActive,
  onFilterApply,
  onReset,
}) => {
  const { t } = useTranslation();
  const { textPrimaryMode } = useThemeColors();

  console.log("filterParams", filterParams);
  const displayAgeRange = (minAge, maxAge) => {
    // Check if both minAge and maxAge are null (representing no specific range)
    if (minAge === null && maxAge === null) {
      // console.log("filter.allAges");
      return t("filter.allAges"); // All ages if no specific range is set
    }
    // Check if only minAge is set
    else if (minAge !== null && maxAge === null) {
      // console.log("filter.fromAge");
      return t("filter.fromAge", { age: minAge }); // Only minAge set
    }
    // Check if only maxAge is set
    else if (minAge === null && maxAge !== null) {
      // console.log("filter.upToAge");
      return t("filter.upToAge", { age: maxAge }); // Only maxAge set
    }
    // If both minAge and maxAge are set to specific values, log and return range
    else {
      // console.log("filter.fromToAge");
      return t("filter.fromToAge", { min: minAge, max: maxAge }); // Both minAge and maxAge set
    }
  };

  // Helper function to display status as a string
  const displayStatus = (status) => {
    // Ensure status is always an array
    if (typeof status === "string") {
      status = status.split(",").map((s) => s.trim());
    }

    if (!Array.isArray(status) || status.length === 0) {
      return ""; // No status selected
    }

    return status
      .map((s) => t(`patientStatus.${s.trim()}`)) // Ensure translation key matches and trims any whitespace
      .join(", ");
  };

  // Helper function to display state as a string
  // const displayState = (state) => {
  //   if (!state || state.length === 0) {
  //     return t("filter.none"); // No state selected
  //   }
  //   return state;
  // };
  // Helper function to display state as a string
  const displayState = (state) => {
    // Ensure state is always an array
    if (typeof state === "string") {
      state = state.split(",").map((s) => s.trim());
    }

    if (!Array.isArray(state) || state.length === 0) {
      return t("filter.none"); // No state selected
    }

    return state
      .map((s) => s.trim()) // Ensure translation key matches and trims any whitespace
      .join(", ");
  };

  // Helper function to format and display dates using dayjs
  const displayDateRange = (startDate, endDate) => {
    if (!startDate && !endDate) {
      return ""; // No dates selected
    } else if (startDate && !endDate) {
      return t("filter.singleDate", {
        date: dayjs(startDate).format("LL"), // Uses dayjs locale format
      });
    } else if (!startDate && endDate) {
      return t("filter.singleDate", {
        date: dayjs(endDate).format("LL"), // Uses dayjs locale format
      });
    } else if (
      startDate &&
      endDate &&
      dayjs(startDate).isSame(dayjs(endDate), "day")
    ) {
      // If start and end dates are the same, show a single date
      return t("filter.singleDate", {
        date: dayjs(startDate).format("LL"),
      });
    } else {
      return t("filter.dateRange", {
        start: dayjs(startDate).format("LL"),
        end: dayjs(endDate).format("LL"),
      });
    }
  };

  const clearFilter = () => {
    // console.log("RESET IN ACTIVEFILTER PRESSED");

    const resetState = {
      minAge: null,
      maxAge: null,
      startDate: "",
      endDate: "",
      status: [],
      state: [],
    };

    // Apply the reset state using the onFilterApply function passed as a prop
    onFilterApply(resetState);

    // Trigger the reset in PatientFilterPopover through the prop
    if (typeof onReset === "function") {
      onReset();
    }

    // Update the filter active state
    setIsFilterActive(false);

    // Save the reset state in local storage
    localStorage.setItem("filterSettings_patients", JSON.stringify(resetState));
  };

  // Logs
  //   console.log("Filter Params:", filterParams);

  return (
    <>
      <Flex w="100%" align="center" justify="space-between" p="0rem 2rem 1rem">
        <Flex align="flex-start" w="100%">
          <Flex align="center">
            <Icon
              as={FaCircleInfo}
              fontSize="0.875rem"
              color={primaryAlt}
              position="relative"
              top="-1px"
            />
            <Text
              fontSize="0.8rem"
              color={textSecondary}
              fontWeight="500"
              ml="0.5rem"
              whiteSpace="nowrap"
            >
              {t("filter.filterActive")}.
            </Text>
          </Flex>

          <Flex align="center" gap={1}>
            <Text
              fontSize="0.8rem"
              color={textSecondary}
              fontWeight="500"
              ml="0.25rem"
              pr="2rem"
            >
              {t("filter.showingPatients")}

              {(() => {
                let first = true; // Keeps track of whether it's the first filter applied

                const addComma = () => {
                  if (!first) return ", "; // Add a comma only if it's NOT the first filter
                  first = false;
                  return "";
                };
                return (
                  <>
                    {filterParams.status && filterParams.status.length > 0 && (
                      <>
                        <Text
                          as="span"
                          fontSize="0.8rem"
                          color={textSecondary}
                          fontWeight="500"
                          mr={1}
                        >
                          {addComma()}
                          {t("filter.withStatus")}
                        </Text>

                        <Text
                          as="span"
                          fontSize="0.8rem"
                          color={textPrimaryMode}
                          fontWeight="500"
                          textTransform="lowercase"
                        >
                          {`${displayStatus(filterParams.status)}`}
                        </Text>
                      </>
                    )}

                    {filterParams.startDate && filterParams.endDate && (
                      <>
                        <Text
                          as="span"
                          fontSize="0.8rem"
                          color={textSecondary}
                          fontWeight="500"
                          mr={1}
                        >
                          {addComma()}
                          {t("filter.date")}
                        </Text>
                        <Text
                          as="span"
                          fontSize="0.8rem"
                          color={textPrimaryMode}
                          fontWeight="500"
                        >
                          {`${displayDateRange(
                            filterParams.startDate,
                            filterParams.endDate
                          )}`}
                        </Text>
                      </>
                    )}

                    <>
                      <Text
                        as="span"
                        fontSize="0.8rem"
                        color={textSecondary}
                        fontWeight="500"
                        mr={1}
                      >
                        {addComma()}
                        {t("filter.age")}
                      </Text>

                      <Text
                        as="span"
                        fontSize="0.8rem"
                        color={textPrimaryMode}
                        fontWeight="500"
                      >
                        {`${displayAgeRange(
                          filterParams.minAge,
                          filterParams.maxAge
                        )}`}
                      </Text>
                    </>

                    {filterParams.state && filterParams.state.length > 0 && (
                      <>
                        <Text
                          as="span"
                          fontSize="0.8rem"
                          color={textSecondary}
                          fontWeight="500"
                          mr={1}
                        >
                          {addComma()}
                          {t("filter.andState")}
                        </Text>
                        <Text
                          as="span"
                          fontSize="0.8rem"
                          color={textPrimaryMode}
                          fontWeight="500"
                          // textTransform="lowercase"
                        >
                          {displayState(filterParams.state)}
                        </Text>
                      </>
                    )}
                  </>
                );
              })()}
            </Text>

            {/* STATUS START */}
            {/* {filterParams &&
            filterParams.status &&
            filterParams.status.length > 0 ? (
              <Flex align="center">
                <Text
                  fontSize="0.8rem"
                  color={textSecondary}
                  fontWeight="500"
                  lineHeight="1.4"
                  mr={1}
                >
                  {t("filter.withStatus")}
                </Text>
                <Text
                  fontSize="0.8rem"
                  color={textPrimaryMode}
                  fontWeight="500"
                  lineHeight="1.4"
                  textTransform="lowercase"
                >
                  {displayStatus(filterParams.status)}
                </Text>
              </Flex>
            ) : null} */}
            {/* STATUS END */}

            {/* DATE START */}
            {/* {filterParams && filterParams.startDate && filterParams.endDate ? (
              <Flex align="center">
                <Text
                  fontSize="0.8rem"
                  color={textSecondary}
                  fontWeight="500"
                  lineHeight="1.4"
                  mr={1}
                >
                  {t("filter.date")}
                </Text>
                <Text
                  fontSize="0.8rem"
                  color={textPrimaryMode}
                  fontWeight="500"
                  lineHeight="1.4"
                >
                  {displayDateRange(
                    filterParams.startDate,
                    filterParams.endDate
                  )}
                </Text>
              </Flex>
            ) : null} */}

            {/* DATE END  */}

            {/* AGE START */}
            {/* {(filterParams.minAge !== 0 || filterParams.maxAge !== 100) && (
              <Flex align="center">
                <Text
                  fontSize="0.8rem"
                  color={textSecondary}
                  fontWeight="500"
                  lineHeight="1.4"
                  mr={1}
                >
                  {t("filter.age")}
                </Text>
                <Text
                  fontSize="0.8rem"
                  color={textPrimaryMode}
                  fontWeight="500"
                  lineHeight="1.4"
                >
                  {displayAgeRange(filterParams.minAge, filterParams.maxAge)}
                </Text>
              </Flex>
            )} */}

            {/* AGE END  */}

            {/* STATE START  */}
            {/* {filterParams &&
              filterParams.state &&
              filterParams.state.length > 0 && (
                <Flex align="center">
                  <Text
                    fontSize="0.8rem"
                    color={textSecondary}
                    fontWeight="500"
                    lineHeight="1.4"
                    mr={1}
                  >
                    {t("filter.andState")}
                  </Text>
                  <Text
                    fontSize="0.8rem"
                    color={textPrimaryMode}
                    fontWeight="500"
                    lineHeight="1.4"
                  >
                    {displayState(filterParams.state)}
                  </Text>
                </Flex>
              )} */}

            {/* STATE END  */}
          </Flex>
        </Flex>

        <Flex align="center" justify="flex-end">
          <Button
            size="sm"
            fontSize="0.8rem"
            borderRadius="10rem"
            bg={backgroundLight}
            color={textSecondary}
            p="0.5rem 1rem"
            onClick={clearFilter}
            _hover={{ bg: primaryAlt, color: white }}
          >
            {t("filter.clearFilter")}
          </Button>
        </Flex>
      </Flex>
    </>
  );
};

export default ActiveFilter;
